import { Box, Pagination, Stack, ThemeProvider } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import PageHeader from 'components/v2/common/pageHeader';
import ViewToggle from 'components/v2/common/viewToggle';
import { useAuth } from 'contexts/ProtectedRoute';
import AdminPanelFilters from 'views/v2/adminPanel/components/adminPanelFilters';
import AdminUsersTable, {
    type AdminUserAction,
} from 'views/v2/adminPanel/components/adminUsersTable';
import ConsultantsPanel from 'views/v2/adminPanel/components/consultantsPanel';
import EditUserProfileForm from 'views/v2/adminPanel/components/editUserProfileForm';
import PermissionsDialog from 'views/v2/adminPanel/components/permissionsDialog';
import ServiceProvidersPanel from 'views/v2/adminPanel/components/serviceProvidersPanel';

import api from 'api';
import type { AdminUsers } from 'api/models/response/AdminUsers';
import type { CompanyData } from 'api/models/response/CompaniesList';
import { ACTIONS } from 'constant';
import { Role } from 'data/constants/roles';
import { useDebouncedValue } from 'hooks/useCheckEmail';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { loadAdminUsers } from 'redux/admin-users/admin-users.middleware';
import { setAlert } from 'redux/alert';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { AdminPanelView, AdminUserProfileFormInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

export default function AdminPanelPage() {
    const dispatch = useDispatch<AppDispatch>();
    const {
        list: adminUsers,
        isLoading,
        page,
        pageSize,
        totalRecords,
    } = useSelector((state: RootState) => state.adminUsers);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<AdminUsers>();
    const { role } = useAuth();
    const { translate } = useTranslate();
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const [activeView, setActiveView] = useState<AdminPanelView>('users');

    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebouncedValue(searchInput, 400);
    const [companies, setCompanies] = useState<CompanyData>({});
    const [selectedCompany, setSelectedCompany] = useState('');
    const [updatingRoleUserId, setUpdatingRoleUserId] = useState<number | null>(null);

    const companyOptions = useMemo(() => {
        const names = Object.values(companies || {}).filter(Boolean) as string[];
        return names.sort((a, b) => a.localeCompare(b));
    }, [companies]);

    const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / (pageSize || 10)));

    const fetchUsers = useCallback(
        (pageVal: number, query: string, companyName: string) => {
            const paginationValues: {
                page: number;
                pageSize: number;
                name?: string;
                company_name?: string;
            } = {
                page: pageVal,
                pageSize: pageSize || 10,
            };
            if (query.trim()) {
                paginationValues.name = query.trim();
            }
            if (companyName) {
                paginationValues.company_name = companyName;
            }
            void dispatch(loadAdminUsers(paginationValues));
        },
        [dispatch, pageSize]
    );

    useEffect(() => {
        let cancelled = false;
        const loadCompanies = async () => {
            try {
                const response = await api.companies.getCompanies();
                if (!cancelled) {
                    setCompanies(response.data || {});
                }
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(error, 'Failed to load companies'),
                            type: 'error',
                        })
                    );
                }
            }
        };
        void loadCompanies();
        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    useEffect(() => {
        fetchUsers(1, debouncedSearch, selectedCompany);
    }, [debouncedSearch, selectedCompany, fetchUsers]);

    const reload = useCallback(() => {
        fetchUsers(page || 1, debouncedSearch, selectedCompany);
    }, [debouncedSearch, fetchUsers, page, selectedCompany]);

    const handlePageChange = (_: React.ChangeEvent<unknown>, nextPage: number) => {
        fetchUsers(nextPage, debouncedSearch, selectedCompany);
    };

    const handleAction = (action: AdminUserAction, user: AdminUsers, extra?: string) => {
        switch (action) {
            case ACTIONS.EDIT:
                setModalObject((prev) => ({
                    ...prev,
                    action: ACTIONS.EDIT,
                    title: 'Edit user profile',
                    data: user,
                    loading: false,
                    disabled: false,
                }));
                break;
            case ACTIONS.RESEND:
                setModalObject((prev) => ({
                    ...prev,
                    action: ACTIONS.RESEND,
                    title: 'Resend invitation?',
                    content: `Resend the invitation email to ${user.email}?`,
                    data: user,
                    loading: false,
                    disabled: false,
                }));
                break;
            case 'RESET_LINK':
                setModalObject((prev) => ({
                    ...prev,
                    action: 'RESET_LINK',
                    title: 'Send password reset link?',
                    content: `Send a password reset link to ${user.email}? The user will set their own password.`,
                    data: user,
                    loading: false,
                    disabled: false,
                }));
                break;
            case 'PERMISSIONS':
                setModalObject((prev) => ({
                    ...prev,
                    action: 'PERMISSIONS',
                    title: 'Permissions',
                    data: user,
                    loading: false,
                    disabled: false,
                }));
                break;
            case 'CHANGE_ROLE':
                if (extra) {
                    void handleRoleChange(user, extra);
                }
                break;
            default:
                break;
        }
    };

    const handleRoleChange = async (user: AdminUsers, nextRole: string) => {
        if (!nextRole || nextRole === user.role) return;
        setUpdatingRoleUserId(user.id);
        try {
            await api.immigrationProfessional.put(user.id, {
                role: nextRole,
            } as never);
            dispatch(setAlert({ message: 'Role updated successfully', type: 'success' }));
            reload();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to update role'),
                    type: 'error',
                })
            );
        } finally {
            setUpdatingRoleUserId(null);
        }
    };

    const handleEditSubmit = async (
        values: AdminUserProfileFormInterface,
        _helpers: FormikHelpers<AdminUserProfileFormInterface>
    ) => {
        if (!modalObject.data) return;
        const user = modalObject.data;
        const payload: { firstname?: string; lastname?: string; email?: string } = {};
        const firstname = values.firstname.trim();
        const lastname = values.lastname.trim();
        const email = values.email.trim();

        if (firstname !== (user.firstname || '').trim()) payload.firstname = firstname;
        if (lastname !== (user.lastname || '').trim()) payload.lastname = lastname;
        if (email !== (user.email || '').trim()) payload.email = email;

        if (!Object.keys(payload).length) {
            onResetModalObj();
            return;
        }

        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
        try {
            await api.admin.updateUserProfile(user.id, payload);
            dispatch(setAlert({ message: 'Profile updated successfully', type: 'success' }));
            onResetModalObj();
            reload();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to update profile'),
                    type: 'error',
                })
            );
        }
    };

    const handleConfirmResend = async () => {
        if (!modalObject.data?.id) return;
        setModalObject((prev) => ({ ...prev, loading: true }));
        try {
            await api.sendInvitation.sendInvitation(modalObject.data.id);
            dispatch(setAlert({ message: 'Invitation email sent successfully', type: 'success' }));
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to resend invitation'),
                    type: 'error',
                })
            );
        }
    };

    const handleConfirmResetLink = async () => {
        if (!modalObject.data?.email) return;
        setModalObject((prev) => ({ ...prev, loading: true }));
        try {
            await api.forgotPassword.post({
                email: modalObject.data.email.trim().toLowerCase(),
            });
            dispatch(setAlert({ message: 'Password reset link sent', type: 'success' }));
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false }));
            const status =
                error && typeof error === 'object' && 'response' in error
                    ? (error as { response?: { status?: number } }).response?.status
                    : undefined;
            dispatch(
                setAlert({
                    message:
                        status === 429
                            ? 'Too many requests. Please try again later.'
                            : getErrorMessage(error, 'Failed to send reset link'),
                    type: 'error',
                })
            );
        }
    };

    const viewToggle = isSuperSuperAdmin ? (
        <ViewToggle
            ariaLabel={translate('adminPanel')}
            sx={{
                width: 'fit-content',
                maxWidth: '100%',
                flex: '0 1 auto',
                justifyContent: 'flex-start',
                overflowX: 'auto',
                '& > button': { px: { xs: 1.25, sm: 2 } },
            }}
            options={[
                { id: 'users', label: translate('users') },
                { id: 'serviceProviders', label: translate('serviceProviders') },
                { id: 'consultants', label: translate('consultants') },
            ]}
            value={activeView}
            onChange={setActiveView}
        />
    ) : null;

    if (isSuperSuperAdmin && activeView === 'serviceProviders') {
        return (
            <ThemeProvider theme={appThemeV2}>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                    <ServiceProvidersPanel headerControls={viewToggle} />
                </Box>
            </ThemeProvider>
        );
    }

    if (isSuperSuperAdmin && activeView === 'consultants') {
        return (
            <ThemeProvider theme={appThemeV2}>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                    <ConsultantsPanel headerControls={viewToggle} />
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <Stack spacing={2} sx={{ mb: 2 }}>
                    <PageHeader title="adminPanel" titleEnd={viewToggle} sx={{ mb: 0 }} />
                    <AdminPanelFilters
                        companies={companyOptions}
                        selectedCompany={selectedCompany}
                        onCompanyChange={setSelectedCompany}
                        search={searchInput}
                        onSearchChange={setSearchInput}
                    />
                </Stack>

                <AdminUsersTable
                    users={adminUsers || []}
                    loading={isLoading}
                    updatingRoleUserId={updatingRoleUserId}
                    onAction={handleAction}
                />

                {totalRecords > 0 ? (
                    <Stack direction="row" sx={{ mt: 2.5, justifyContent: 'center' }}>
                        <Pagination
                            color="primary"
                            page={page || 1}
                            count={totalPages}
                            onChange={handlePageChange}
                            siblingCount={1}
                            boundaryCount={1}
                        />
                    </Stack>
                ) : null}

                {modalObject.action === ACTIONS.EDIT && modalObject.data ? (
                    <EditUserProfileForm
                        originalEmail={modalObject.data.email}
                        initialValues={{
                            firstname: modalObject.data.firstname || '',
                            lastname: modalObject.data.lastname || '',
                            email: modalObject.data.email || '',
                        }}
                        loading={modalObject.loading || modalObject.disabled}
                        onClose={onResetModalObj}
                        onSubmit={handleEditSubmit}
                    />
                ) : null}

                {modalObject.action === 'PERMISSIONS' && modalObject.data ? (
                    <PermissionsDialog user={modalObject.data} onClose={onResetModalObj} />
                ) : null}

                {modalObject.action === ACTIONS.RESEND ? (
                    <ConfirmationDialog
                        title={modalObject.title}
                        content={modalObject.content}
                        loading={modalObject.loading}
                        onClose={onResetModalObj}
                        onConfirm={handleConfirmResend}
                    />
                ) : null}

                {modalObject.action === 'RESET_LINK' ? (
                    <ConfirmationDialog
                        title={modalObject.title}
                        content={modalObject.content}
                        loading={modalObject.loading}
                        onClose={onResetModalObj}
                        onConfirm={handleConfirmResetLink}
                    />
                ) : null}
            </Box>
        </ThemeProvider>
    );
}
